<template>
	<div class="InputRadio" :class="classes">
		<input
			type="radio"
			:name="name"
			:value="value"
			:id="id + value"
			:checked="shouldBeChecked"
			:disabled="disabled"
			@change="emitEvent"
		/>
		<label :for="id + value">{{ label }}</label>
	</div>
</template>

<script>
export default {
	name: 'CloInputRadio',
	emits: ['change', 'update:checked'],
	data() {
		return {};
	},
	model: {
		prop: 'checked',
		event: 'change',
	},
	props: {
		/**
		 * The radio name attribute
		 */
		name: {
			type: String,
			default: '',
		},
		value: {
			type: String,
			default: '',
		},
		/**
		 * Meant to be used with v-model, can be empty string or one of the possible values
		 */
		checked: {
			type: String,
			default: '',
		},
		id: {
			type: String,
			default: 'id-' + Math.random().toString(36).substr(2, 9),
		},
		label: {
			type: String,
			required: true,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		reverse: {
			type: Boolean,
			default: false,
		},
		pill: {
			type: Boolean,
			default: false,
		},
		button: {
			type: Boolean,
			default: false,
		},
		/**
		 * Size: sm, lg, xl
		 */
		size: {
			type: String,
			default: '',
		},
	},
	computed: {
		classes() {
			const classes = [];
			const prefix = 'InputRadio--';
			if (this.size.length) {
				classes.push(prefix + this.size);
			}
			if (this.reverse) {
				classes.push(prefix + 'reverse');
			}
			if (this.size.length || this.reverse) {
				classes.push(prefix + 'styled');
			}
			if (this.pill) {
				classes.push(prefix + 'pill');
			}
			if (this.button) {
				classes.push(prefix + 'button');
			}
			return classes;
		},
		shouldBeChecked() {
			return this.checked === this.value;
		},
	},
	methods: {
		emitEvent(e) {
			this.$emit('change', this.value); // for use with v-model in Vue 2
			this.$emit('update:checked', this.value); // for use with v-model in Vue 3
		},
	},
};
</script>

<style lang="scss">
.InputRadio {
	display: block;
	input[type='radio'] {
		margin-right: 0.5rem;
		[dir='rtl'] & {
			margin-right: 0;
			margin-left: 0.5rem;
		}
	}
	label {
		display: inline-block;
	}
}

.InputRadio--styled {
	@include stack-xs;
	label {
		@include text(3);
		margin: 0;
		display: inline-flex;
		align-items: center;
		&:before {
			box-sizing: border-box;
			content: '';
			display: block;
			flex: 0 0 auto;
			height: 1rem;
			width: 1rem;
			border: 1px solid $color-form-outline;
			background-color: $color-white;
			@include inline-sm;
			border-radius: 9999px;
		}
	}
	[type='radio'] {
		appearance: none;
		position: absolute;
		opacity: 0;
		margin: 0;
	}
	[type='radio']:checked + label:before {
		background-color: $color-white;
		border-color: $brand-color-ocean-blue-9;
		border-width: 6px;
	}
	[type='radio']:focus + label:before,
	[type='radio']:hover + label:before {
		box-shadow: 0 0 0 2px $color-form-outline-focus;
	}
	[type='radio'][disabled] + label,
	[type='radio'][readonly] + label {
		color: $color-text-muted;
	}
	[type='radio'][disabled] + label:before,
	[type='radio'][readonly] + label:before {
		background-color: $color-neutral-1;
		border-color: $color-neutral-2;
	}
}

.InputRadio--reverse {
	label {
		color: $color-text-reverse;
		&:before {
			border: 1px solid $color-form-outline-reverse;
			background-color: $brand-color-sand-dark-1;
		}
	}
	[type='radio']:focus + label:before,
	[type='radio']:hover + label:before {
		box-shadow: 0 0 0 2px $color-form-outline-focus-reverse;
	}
	[type='radio'][disabled] + label,
	[type='radio'][readonly] + label {
		color: $color-neutral-3;
	}
	[type='radio'][disabled] + label:before,
	[type='radio'][readonly] + label:before {
		background-color: $color-neutral-3;
		border-color: $color-neutral-4;
		opacity: 0.5;
	}
}

.InputRadio--xl {
	@include stack-md;
	label {
		@include text(5);
		&:before {
			height: 2rem;
			width: 2rem;
		}
	}
	[type='radio']:checked + label:before {
		border-width: 12px;
	}
}

.InputRadio--lg {
	@include stack-sm;
	label {
		@include text(4);
		&:before {
			height: 1.25rem;
			width: 1.25rem;
		}
	}
	[type='radio']:checked + label:before {
		border-width: 7px;
	}
}

.InputRadio--sm {
	margin: 0;
	label {
		@include text(2);
	}
}

.InputRadio--pill {
	display: inline-block;
	input[type='radio'] {
		@include inputHidden;
	}
	label {
		display: inline-flex;
		align-items: center;
		text-align: center;
		white-space: nowrap;
		@include inline-xs;
		@include inset-squish-sm;
		@include text(3);
		cursor: pointer;
		font-weight: bold;
		border-radius: 999px;
		color: $color-text;
		background-color: $color-white;
		border: 1px solid $color-form-outline;
	}
	input[type='radio'] {
		& + label {
			color: $color-link;
			background-color: $brand-color-ocean-blue-2;
			border-color: transparent;
		}
		&:hover + label,
		&:focus + label,
		&:active + label {
			color: $color-link-hover;
			background-color: $brand-color-ocean-blue-3;
			border-color: $color-link-hover;
		}
		&:checked + label,
		&:checked:hover + label,
		&:checked:focus + label,
		&:checked:active + label {
			color: $color-link-special;
			background-color: $brand-color-forest-green-3;
			border-color: $color-link-special;
		}
	}
}

.InputRadio--button {
	flex: 1 0 auto;
	margin: 0;
	input[type='radio'] {
		border: 0;
		clip: rect(0 0 0 0);
		height: 1px;
		margin: -1px;
		overflow: hidden;
		padding: 0;
		position: absolute;
		width: 1px;
	}
	label {
		width: 100%;
		padding: $space-sm;
		font-size: 1rem;
		line-height: 1.5;
		align-items: center;
		text-align: center;
		white-space: nowrap;
		cursor: pointer;
		background-color: transparent;
		border-width: 1px;
		border-style: solid;
		border-color: $color-form-outline;
		border-radius: 0;
		font-weight: normal;
		margin: 0;
	}
	&:first-child label {
		border-top-left-radius: $radius-sm;
		border-bottom-left-radius: $radius-sm;
		[dir='rtl'] & {
			border-top-left-radius: 0;
			border-bottom-left-radius: 0;
			border-top-right-radius: $radius-sm;
			border-bottom-right-radius: $radius-sm;
		}
	}
	&:last-child label {
		border-top-right-radius: $radius-sm;
		border-bottom-right-radius: $radius-sm;
		[dir='rtl'] & {
			border-top-right-radius: 0;
			border-bottom-right-radius: 0;
			border-top-left-radius: $radius-sm;
			border-bottom-left-radius: $radius-sm;
		}
	}
	input[type='radio']:hover + label {
		color: $color-text;
	}
	input[type='radio']:checked + label {
		color: $color-link;
		border-color: $color-form-outline-focus;
		background-color: $color-white;
	}
}
</style>
